import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Entitlements } from '@snap-platform/shared';
import { useAuth } from '../context/AuthContext.js';
import { useSnap } from '../hooks/useSnap.js';
import {
  restoreSubscription,
  getEntitlements,
  getSubscriptionStatus,
} from '../services/api.js';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  LockIcon,
  WalletIcon,
  PlugIcon,
} from '../components/Icons.js';

// ---------------------------------------------------------------------------
// Status dot (shared pattern)
// ---------------------------------------------------------------------------
function StatusDot({ status }: { status: 'green' | 'yellow' | 'red' }) {
  return <span className={`sp-status-dot sp-status-dot-${status}`} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Step indicator (reuses rc-step classes from Recover page)
// ---------------------------------------------------------------------------
function StepIndicator({
  number,
  label,
  active,
  done,
}: {
  number: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  const className = done
    ? 'rc-step rc-step-done'
    : active
      ? 'rc-step rc-step-active'
      : 'rc-step';

  return (
    <div className={className}>
      <span className="rc-step-num">
        {done ? '\u2713' : number}
      </span>
      <span className="rc-step-label">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type TransferStep = 'connect' | 'ready' | 'confirm' | 'processing' | 'success' | 'not_found';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function TransferSubscription() {
  const { token, walletAddress, isAuthenticated, connectWallet, isConnecting } = useAuth();
  const snap = useSnap(token);
  const [step, setStep] = useState<TransferStep>(isAuthenticated ? 'ready' : 'connect');
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<{
    tier: string;
    stripeSubscriptionId?: string;
  } | null>(null);

  // Live entitlements & subscription state
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [subStatus, setSubStatus] = useState<string | null>(null);

  // Progress steps (visual only)
  const [progressStep, setProgressStep] = useState(0);

  useEffect(() => {
    if (!token) return;
    getEntitlements(token).then(setEntitlements).catch(() => {});
    getSubscriptionStatus(token).then((s) => setSubStatus(s.status)).catch(() => {});
  }, [token]);

  async function handleConnect() {
    setError(null);
    try {
      await connectWallet();
      setStep('ready');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect wallet');
    }
  }

  async function handleTransfer() {
    if (!token) return;
    setStep('processing');
    setProgressStep(1);
    setError(null);
    try {
      // Visual: brief pause for "Verifying subscription" step
      await new Promise((r) => setTimeout(r, 800));
      setProgressStep(2);
      const res = await restoreSubscription(token);
      if (res.restored) {
        setResult({ tier: res.tier, stripeSubscriptionId: res.stripeSubscriptionId });
        // Refresh entitlements
        if (token) {
          getEntitlements(token).then(setEntitlements).catch(() => {});
          setSubStatus('active');
        }
        setStep('success');
      } else {
        setResult(null);
        setStep('not_found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transfer failed');
      setStep('ready');
    }
    setProgressStep(0);
  }

  function truncateAddress(address: string): string {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  // Derived state
  const isPro = entitlements?.tier === 'pro';
  const isSubActive = subStatus === 'active';
  const snapOk = snap.isInstalled;

  const isTerminal = step === 'success' || step === 'not_found';

  const stepNumber =
    step === 'connect' ? 1
      : step === 'ready' ? 2
        : step === 'confirm' || step === 'processing' ? 3
          : 4;

  // Banner status
  const bannerStatus: 'green' | 'yellow' | 'red' = !isAuthenticated
    ? 'red'
    : isPro && isSubActive
      ? 'green'
      : 'yellow';

  return (
    <div className="recover-page">
      <div className="container rc-container">

        {/* ================================================================ */}
        {/* PART 1 — PROTECTION STATUS BANNER                               */}
        {/* ================================================================ */}
        <div className={`dash-card rc-banner rc-banner-${bannerStatus}`}>
          <div className="rc-banner-content">
            <ShieldCheckIcon className="rc-banner-icon" aria-hidden="true" />
            <div>
              <h3 className="rc-banner-title">Protection Status</h3>
              <p className="rc-banner-desc">
                {bannerStatus === 'green'
                  ? 'Your wallet is fully protected.'
                  : bannerStatus === 'yellow'
                    ? 'Limited protection. Transfer or restore your Pro subscription.'
                    : 'Connect your wallet to check your protection status.'}
              </p>
            </div>
          </div>
          <div className="rc-banner-checks">
            <span className="rc-banner-check">
              <StatusDot status={isAuthenticated ? 'green' : 'red'} />
              Wallet {isAuthenticated ? 'Connected' : 'Not Connected'}
            </span>
            <span className="rc-banner-check">
              <StatusDot status={isPro && isSubActive ? 'green' : isPro ? 'yellow' : 'red'} />
              {isPro ? (isSubActive ? 'Pro Active' : 'Pro Inactive') : 'Free'}
            </span>
            <span className="rc-banner-check">
              <StatusDot status={snapOk ? 'green' : 'yellow'} />
              Snap {snapOk ? 'Installed' : 'Not Installed'}
            </span>
            {entitlements && (
              <span className="rc-banner-check">
                <StatusDot status={entitlements.scansRemaining > 0 ? 'green' : 'red'} />
                {entitlements.scansRemaining} / {entitlements.monthlyLimit} Scans
              </span>
            )}
          </div>
        </div>

        {/* ================================================================ */}
        {/* PART 2 — WHEN TO USE TRANSFER                                   */}
        {/* ================================================================ */}
        {!isTerminal && (
          <div className="dash-card">
            <div className="dash-card-header">
              <WalletIcon className="dash-section-icon" aria-hidden="true" />
              <h2>Transfer Your Subscription</h2>
            </div>
            <p className="dash-card-subtitle">Use Transfer when:</p>
            <ul className="rc-use-list">
              <li>You created a new wallet address</li>
              <li>You want to move your Pro plan to this wallet</li>
              <li>You no longer want Pro attached to your old wallet</li>
            </ul>
            <div className="ts-restore-hint">
              <AlertTriangleIcon className="ts-restore-hint-icon" aria-hidden="true" />
              <div>
                <p>
                  <strong>Using the same wallet on a new computer?</strong> Use Restore instead — it's faster and doesn't require a transfer.
                </p>
                <Link to="/recover" className="btn btn-secondary btn-sm" style={{ marginTop: '0.5rem' }}>
                  Use Restore Instead
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Error banner */}
        {error && !isTerminal && (
          <div className="rc-error-banner">
            <AlertTriangleIcon className="rc-error-banner-icon" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {/* Step indicators */}
        {!isTerminal && step !== 'confirm' && (
          <div className="rc-steps-row">
            <StepIndicator number={1} label="Connect" active={step === 'connect'} done={stepNumber > 1} />
            <StepIndicator number={2} label="Review" active={step === 'ready'} done={stepNumber > 2} />
            <StepIndicator number={3} label="Transfer" active={step === 'processing'} done={stepNumber > 3} />
          </div>
        )}

        {/* ================================================================ */}
        {/* PART 3 — PRIMARY TRANSFER CARD                                  */}
        {/* ================================================================ */}
        {step !== 'confirm' && (
          <div className="dash-card rc-card">
            {/* Step 1: Connect */}
            {step === 'connect' && (
              <>
                <div className="rc-card-step-label">Step 1</div>
                <h2 className="rc-card-title">Connect Your Wallet</h2>
                <p className="rc-card-desc">
                  Connect the wallet you want to transfer your Pro subscription to. We'll search for any active Stripe subscription linked to your account.
                </p>
                <button className="btn btn-primary rc-btn-lg" onClick={handleConnect} disabled={isConnecting}>
                  <WalletIcon className="rc-btn-icon" aria-hidden="true" />
                  {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                </button>
              </>
            )}

            {/* Step 2: Ready */}
            {step === 'ready' && (
              <>
                <div className="rc-card-step-label">Step 2</div>
                <h2 className="rc-card-title">Review & Transfer</h2>
                {walletAddress && (
                  <div className="ts-wallet-display">
                    <WalletIcon className="ts-wallet-display-icon" aria-hidden="true" />
                    <div>
                      <span className="ts-wallet-display-label">Connected Wallet</span>
                      <code className="ts-wallet-display-addr">{truncateAddress(walletAddress)}</code>
                    </div>
                  </div>
                )}
                <p className="rc-card-desc">
                  We'll search for any active Stripe subscription and link it to this wallet. This does not move funds — it only moves CryptoGuardians Pro access.
                </p>
                <label className="ts-confirm-check">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  <span>I understand this will link my Pro access to the connected wallet.</span>
                </label>
                <button
                  className="btn btn-primary rc-btn-lg"
                  onClick={() => setStep('confirm')}
                  disabled={!confirmed}
                >
                  Transfer Subscription
                </button>
              </>
            )}

            {/* PART 5 — LOADING + PROGRESS */}
            {step === 'processing' && (
              <div className="rc-loading">
                <div className="rc-spinner" />
                <h2 className="rc-card-title">Transferring your Pro access...</h2>
                <div className="ts-progress">
                  <div className={`ts-progress-step ${progressStep >= 1 ? 'ts-progress-active' : ''}`}>
                    <StatusDot status={progressStep >= 2 ? 'green' : progressStep >= 1 ? 'yellow' : 'red'} />
                    <span>Verifying subscription</span>
                  </div>
                  <div className={`ts-progress-step ${progressStep >= 2 ? 'ts-progress-active' : ''}`}>
                    <StatusDot status={progressStep >= 2 ? 'yellow' : 'red'} />
                    <span>Completing transfer</span>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================ */}
            {/* PART 6 — SUCCESS STATE                                       */}
            {/* ============================================================ */}
            {step === 'success' && result && (
              <div className="rc-result rc-result-success">
                <CheckCircleIcon className="rc-result-icon rc-result-icon-success" aria-hidden="true" />
                <h2 className="rc-card-title">Transfer Complete</h2>
                <p className="rc-card-desc">
                  Your Pro subscription has been linked to this wallet.
                </p>
                <div className="dash-kv-list rc-result-details">
                  {walletAddress && (
                    <div className="dash-kv-row">
                      <span className="dash-kv-label">Pro access on</span>
                      <span className="dash-kv-value" style={{ fontFamily: 'monospace' }}>
                        {truncateAddress(walletAddress)}
                      </span>
                    </div>
                  )}
                  <div className="dash-kv-row">
                    <span className="dash-kv-label">Plan</span>
                    <span className="dash-kv-value sp-status-inline">
                      <StatusDot status="green" />
                      Pro
                    </span>
                  </div>
                  {entitlements && (
                    <div className="dash-kv-row">
                      <span className="dash-kv-label">Scans Remaining</span>
                      <span className="dash-kv-value">
                        {entitlements.scansRemaining} / {entitlements.monthlyLimit}
                      </span>
                    </div>
                  )}
                  <div className="dash-kv-row">
                    <span className="dash-kv-label">Snap Status</span>
                    <span className="dash-kv-value sp-status-inline">
                      <StatusDot status={snapOk ? 'green' : 'yellow'} />
                      {snapOk ? 'Installed' : 'Not Installed'}
                    </span>
                  </div>
                </div>
                <div className="ts-success-actions">
                  <Link to="/wallet" className="btn btn-primary rc-btn-lg">
                    Go to Dashboard
                  </Link>
                  {!snapOk && snap.isMetaMask && (
                    <button
                      className="btn btn-secondary rc-btn-lg"
                      onClick={() => snap.installSnap()}
                      disabled={snap.isLoading}
                    >
                      <PlugIcon className="rc-btn-icon" aria-hidden="true" />
                      {snap.isLoading ? 'Installing...' : 'Install Snap'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ============================================================ */}
            {/* PART 7 — FAILURE STATE                                       */}
            {/* ============================================================ */}
            {step === 'not_found' && (
              <div className="rc-result rc-result-warning">
                <AlertTriangleIcon className="rc-result-icon rc-result-icon-warning" aria-hidden="true" />
                <h2 className="rc-card-title">Transfer Not Completed</h2>
                <p className="rc-card-desc">
                  We could not find an active subscription to transfer. This can happen if:
                </p>
                <ul className="rc-use-list rc-failure-list">
                  <li>The old wallet does not have an active Pro subscription</li>
                  <li>You are not connected to the correct wallet</li>
                  <li>The subscription was cancelled or expired</li>
                  <li>A network issue prevented the search</li>
                </ul>
                <div className="rc-btn-row">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setConfirmed(false);
                      setError(null);
                      setStep('ready');
                    }}
                  >
                    Try Again
                  </button>
                  <Link to="/recover" className="btn btn-secondary">
                    Restore Subscription
                  </Link>
                  <Link to="/support" className="btn btn-secondary">
                    Contact Support
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* PART 4 — CONFIRMATION MODAL                                     */}
        {/* ================================================================ */}
        {step === 'confirm' && (
          <div className="ts-modal-overlay">
            <div className="ts-modal">
              <h2 className="ts-modal-title">Confirm Transfer</h2>
              <div className="ts-modal-body">
                {walletAddress && (
                  <div className="ts-modal-detail">
                    <span className="ts-modal-detail-label">Transfer to wallet</span>
                    <code className="ts-modal-detail-value">{truncateAddress(walletAddress)}</code>
                  </div>
                )}
                <div className="ts-modal-warning">
                  <AlertTriangleIcon className="ts-modal-warning-icon" aria-hidden="true" />
                  <span>Make sure this is the correct wallet. The transfer links your Pro subscription to this address.</span>
                </div>
              </div>
              <div className="ts-modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep('ready')}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleTransfer}
                >
                  Confirm Transfer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* PART 8 — TRUST SECTION                                          */}
        {/* ================================================================ */}
        <div className="dash-card">
          <div className="dash-card-header">
            <LockIcon className="dash-section-icon" aria-hidden="true" />
            <h2>Security & Privacy</h2>
          </div>
          <div className="rc-trust-grid">
            <div className="rc-trust-item">
              <ShieldCheckIcon className="rc-trust-icon" aria-hidden="true" />
              <div>
                <h4>Non-Custodial</h4>
                <p>We never control your funds or private keys.</p>
              </div>
            </div>
            <div className="rc-trust-item">
              <LockIcon className="rc-trust-icon" aria-hidden="true" />
              <div>
                <h4>Permission-Based</h4>
                <p>Transfers require your wallet to be connected and authenticated.</p>
              </div>
            </div>
            <div className="rc-trust-item">
              <WalletIcon className="rc-trust-icon" aria-hidden="true" />
              <div>
                <h4>No Passwords</h4>
                <p>Your wallet signature is your identity.</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
